<script setup>
import {computed, ref, watch} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useModalsStore} from '@/stores/modals'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useViewStore} from '@/stores/view'
import FormControlCategoryTitle from '@/common/form-control/FormControlCategoryTitle.vue'
import Button from '@/common/Button.vue'

const props = defineProps({
    viewId: String,
    filterKey: Number
})

const emit = defineEmits(['input'])

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Stores
const componentsStore = useComponentsStore()
const modalsStore = useModalsStore()
const componentStructureStore = useComponentStructureStore()

const categoryTableName = ref(null)

const filter = computed(() => {
    return viewStore.filters?.[props.filterKey] || null
})

const tableName = computed(() => {
    return viewStore.tableName || null
})

const componentId = computed(() => {
    if (!tableName.value) return null
    return componentsStore.componentIds?.[tableName.value] || null
})

const setCategoryTableName = () => {
    // Guard: Stop execution if filter or component data isn't loaded yet
    if (!filter.value || !componentId.value) return

    const structureData = componentStructureStore.get(componentId.value)
    if (!structureData) return

    const structure = structureData.find(o => o.columnName === filter.value.columnName)
    if (!structure) return

    const component = componentsStore.items.find(o => o.id === structure.categoryComponentId)

    if (component) {
        categoryTableName.value = component.tableName
    }
}

// Watcher replacement for reactive state updates
watch(filter, () => {
    setCategoryTableName()
}, {deep: true, immediate: true})

const selectCategory = async () => {
    if (!filter.value || !categoryTableName.value) return

    const id = filter.value.value
    const modalObj = await modalsStore.show({
        componentName: 'SelectListModal',
        obj: {
            listingName: `${categoryTableName.value}.${filter.value.columnName}`,
            selectedIds: id ? [id] : [],
            onSelect: (selectedIds) => {
                const selectedId = selectedIds.pop()

                // Updates the value of the parent's v-model
                emit('update:modelValue', selectedId || undefined)

                if (modalObj?.id !== undefined) {
                    modalsStore.remove(modalObj.id)
                }
            }
        }
    })
}
</script>

<template>
    <Button v-if="categoryTableName" @click="selectCategory" class="btn-sm border">
        <span v-if="!filter.value">Browse…</span>
        <FormControlCategoryTitle v-else :id="filter.value" :table-name="categoryTableName"/>
    </Button>
</template>